"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import React, { FC, useMemo } from "react";

import { ILink } from "@types";
import { Locale } from "consts/locale";
import { getLink, getTranslatedHref } from "utils/link";

interface TranslatedLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Pre-built link (use `record` instead for internal CMS records that need locale-aware slugs, e.g. nested offices). */
  link?: ILink;
  /** When set, `getLink(record, locale)` is used so office URLs resolve slugs for the active locale. */
  record?: Parameters<typeof getLink>[0];
  children?: React.ReactNode;
}

export const TranslatedLink: FC<TranslatedLinkProps> = ({
  link,
  record,
  children,
  className,
  ...props
}) => {
  const params = useParams();
  const locale = (params?.locale as Locale) ?? "nl";

  const resolvedLink = useMemo(() => {
    if (record) return getLink(record, locale);
    if (link) return link;
    return { href: "" } as ILink;
  }, [record, link, locale]);

  const translatedHref = useMemo(
    () => getTranslatedHref(resolvedLink, locale),
    [resolvedLink, locale],
  );

  return (
    <Link href={translatedHref} className={className} {...props}>
      {children}
    </Link>
  );
};
